<script setup lang="ts">
import { toRef } from 'vue';

import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VDownloadReportButton from '@modules/download-report/components/VDownloadReportButton/VDownloadReportButton.vue';
import VRejectPaymentSystemPopup from '@modules/payment-system-details/components/VRejectPaymentSystemPopup/VRejectPaymentSystemPopup.vue';
import VReviewPaymentSystemButtons from '@modules/payment-system-details/components/VReviewPaymentSystemButtons/VReviewPaymentSystemButtons.vue';
import { usePaymentSystemTableList } from '@modules/payment-system-details/composables';
import {
  ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS_LIST_VIEW_ROUTE_NAME,
  PAYMENT_SYSTEM_DETAILS_PERMISSIONS,
  TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS,
} from '@modules/payment-system-details/constants';
import { EPaymentSystemsTableListType, EReviewButtonVariants, EReviewPaymentSystemActionType } from '@modules/payment-system-details/types';
import VTable from '@modules/tables/components/VTable/VTable.vue';

const {
  rejectionReason,
  data,
  tableState,
  isLoading,
  isFetching,
  updateTableState,
  refreshTableData,
  onConfirmSingle,
  isSingleConfirmLoading,
  isMassConfirmLoading,
  isReportLoading,
  onDownloadReport,
  columnOptions,
  tableFilterOptions,
  multiselectOptions,
  handleMultiselectAction,
} = usePaymentSystemTableList(
  toRef(EPaymentSystemsTableListType.PENDING_LIST),
);
</script>

<template>
  <VCard v-data-qa-auto-id="'PaymentSystemDetailsPendingListPage'" type="table">
    <VTable
      :data="data"
      :title-t-path="$getTPath(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, 'PENDING_REQUESTS_HEADER')"
      :filter-props="tableFilterOptions"
      :multiselect="multiselectOptions"
      :loading="isLoading"
      :state="tableState"
      :is-fetching="isFetching"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @multiselect-action="handleMultiselectAction"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VReviewPaymentSystemButtons
          :id="listItem.id"
          :variant="EReviewButtonVariants.TEXT"
          :disabled="isSingleConfirmLoading || isMassConfirmLoading"
          @approve="onConfirmSingle($event, EReviewPaymentSystemActionType.APPROVE)"
          @reject="onConfirmSingle($event, EReviewPaymentSystemActionType.REJECT)"
        />
        <VIconButton
          v-if="$hasPermission(PAYMENT_SYSTEM_DETAILS_PERMISSIONS.SHOW)"
          icon="pi-id-card"
          :disabled="isSingleConfirmLoading || isMassConfirmLoading"
          :to="{ name: ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS_LIST_VIEW_ROUTE_NAME, params: { id: listItem.id } }"
        />
      </template>
      <template #footerButtons>
        <VDownloadReportButton
          v-if="$hasPermission(PAYMENT_SYSTEM_DETAILS_PERMISSIONS.PENDING_DOWNLOAD_LIST)"
          :label="$getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, 'DOWNLOAD_REPORT')"
          :loading="isReportLoading"
          @download="onDownloadReport"
        />
      </template>
    </VTable>
  </VCard>

  <PConfirmPopup group="confirmPopupPaymentSystemReview">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <VRejectPaymentSystemPopup
        v-model:rejection-reason="rejectionReason"
        :options="message"
        type="popup"
        @close="rejectCallback"
        @submit="acceptCallback"
      />
    </template>
  </PConfirmPopup>

  <PConfirmDialog
    group="confirmDialogPaymentSystemReview"
    class="max-w-[calc(100vw-40px)] md:min-w-[450px]"
    :draggable="false"
  >
    <template #container="{ message, acceptCallback, rejectCallback }">
      <VRejectPaymentSystemPopup
        v-model:rejection-reason="rejectionReason"
        :options="message"
        type="dialog"
        @close="rejectCallback"
        @submit="acceptCallback"
      />
    </template>
  </PConfirmDialog>

  <RouterView
    v-slot="{ Component }"
    name="PaymentSystemDetailsView"
  >
    <component
      :is="Component"
      :locale-key="data?.locale_key"
      :rejection-reason="rejectionReason"
    />
  </RouterView>
</template>
